import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Client, ClientFormData } from '../models/Client';

export class ClientService {
  private static instance: ClientService;
  private clientsCollection = collection(db, 'clients');

  private constructor() {}

  static getInstance(): ClientService {
    if (!ClientService.instance) {
      ClientService.instance = new ClientService();
    }
    return ClientService.instance;
  }

  async createClient(
    companyId: string,
    createdBy: string,
    data: ClientFormData
  ): Promise<Client> {
    const clientRef = doc(this.clientsCollection);
    const now = new Date();

    const client: Client = {
      id: clientRef.id,
      companyId,
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : undefined,
      guardianName: data.guardianName,
      guardianPhone: data.guardianPhone,
      guardianEmail: data.guardianEmail,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      assignedRbtIds: [],
      notes: data.notes,
      status: 'active',
      createdAt: now,
      updatedAt: now,
      createdBy,
    };

    const firestoreData: any = {
      id: clientRef.id,
      companyId,
      firstName: data.firstName,
      lastName: data.lastName,
      guardianName: data.guardianName,
      guardianPhone: data.guardianPhone,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      assignedRbtIds: [],
      status: 'active',
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      createdBy,
    };

    // Add optional fields
    if (data.dateOfBirth) {
      firestoreData.dateOfBirth = Timestamp.fromDate(new Date(data.dateOfBirth));
    }
    if (data.guardianEmail) {
      firestoreData.guardianEmail = data.guardianEmail;
    }
    if (data.notes) {
      firestoreData.notes = data.notes;
    }
    this.applyExtendedFields(firestoreData, data);

    await setDoc(clientRef, firestoreData);
    return client;
  }

  async getClient(clientId: string): Promise<Client | null> {
    const clientRef = doc(this.clientsCollection, clientId);
    const clientSnap = await getDoc(clientRef);

    if (!clientSnap.exists()) {
      return null;
    }

    const data = clientSnap.data();
    return this.mapFirestoreToClient(clientSnap.id, data);
  }

  async getClientsByCompany(companyId: string): Promise<Client[]> {
    const q = query(
      this.clientsCollection,
      where('companyId', '==', companyId),
      orderBy('lastName', 'asc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapFirestoreToClient(doc.id, doc.data())
    );
  }

  async getClientsByRbt(rbtId: string): Promise<Client[]> {
    const q = query(
      this.clientsCollection,
      where('assignedRbtIds', 'array-contains', rbtId),
      where('status', '==', 'active')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapFirestoreToClient(doc.id, doc.data())
    );
  }

  async updateClient(clientId: string, updates: Partial<ClientFormData>): Promise<void> {
    const clientRef = doc(this.clientsCollection, clientId);
    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.fromDate(new Date()),
    };

    // Handle date conversion if present
    if (updates.dateOfBirth) {
      updateData.dateOfBirth = Timestamp.fromDate(new Date(updates.dateOfBirth));
    }
    // Re-normalize the extended fields (converts authorizedHours dates, drops undefined)
    this.applyExtendedFields(updateData, updates);

    await updateDoc(clientRef, updateData);
  }

  // Adds the unified-schema extension fields to a Firestore write object,
  // converting authorizedHours ISO dates to Timestamps and skipping undefined.
  private applyExtendedFields(target: any, data: Partial<ClientFormData>): void {
    if (data.assignedBcbaId !== undefined) {
      target.assignedBcbaId = data.assignedBcbaId;
      target.primarySupervisorId = data.assignedBcbaId; // keep the legacy field in sync
    }
    if (data.preferredLanguage !== undefined) target.preferredLanguage = data.preferredLanguage;
    if (data.latitude !== undefined) target.latitude = data.latitude;
    if (data.longitude !== undefined) target.longitude = data.longitude;
    if (data.diagnosis !== undefined) target.diagnosis = data.diagnosis;
    if (data.authorizedHours !== undefined) {
      const ah = data.authorizedHours;
      target.authorizedHours = {
        weeklyHours: ah.weeklyHours,
        payorName: ah.payorName || '',
        authorizationNumber: ah.authorizationNumber || '',
        authorizationStart: ah.authorizationStart ? Timestamp.fromDate(new Date(ah.authorizationStart)) : null,
        authorizationEnd: ah.authorizationEnd ? Timestamp.fromDate(new Date(ah.authorizationEnd)) : null,
      };
    }
  }

  async assignRbtToClient(clientId: string, rbtId: string): Promise<void> {
    const client = await this.getClient(clientId);
    if (!client) {
      throw new Error('Client not found');
    }

    if (!client.assignedRbtIds.includes(rbtId)) {
      const updatedRbtIds = [...client.assignedRbtIds, rbtId];
      await updateDoc(doc(this.clientsCollection, clientId), {
        assignedRbtIds: updatedRbtIds,
        updatedAt: Timestamp.fromDate(new Date()),
      });
    }
  }

  async removeRbtFromClient(clientId: string, rbtId: string): Promise<void> {
    const client = await this.getClient(clientId);
    if (!client) {
      throw new Error('Client not found');
    }

    const updatedRbtIds = client.assignedRbtIds.filter((id) => id !== rbtId);
    await updateDoc(doc(this.clientsCollection, clientId), {
      assignedRbtIds: updatedRbtIds,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  async setClientStatus(
    clientId: string,
    status: 'active' | 'inactive' | 'discharged'
  ): Promise<void> {
    const clientRef = doc(this.clientsCollection, clientId);
    await updateDoc(clientRef, {
      status,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  async deleteClient(clientId: string): Promise<void> {
    const clientRef = doc(this.clientsCollection, clientId);
    await deleteDoc(clientRef);
  }

  private mapFirestoreToClient(id: string, data: any): Client {
    return {
      id,
      companyId: data.companyId,
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: data.dateOfBirth?.toDate(),
      guardianName: data.guardianName,
      guardianPhone: data.guardianPhone,
      guardianEmail: data.guardianEmail,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      diagnosis: Array.isArray(data.diagnosis) ? data.diagnosis : undefined,
      preferredLanguage: data.preferredLanguage,
      authorizedHours: data.authorizedHours
        ? {
            weeklyHours: data.authorizedHours.weeklyHours,
            payorName: data.authorizedHours.payorName,
            authorizationNumber: data.authorizedHours.authorizationNumber,
            authorizationStart: data.authorizedHours.authorizationStart?.toDate?.() ?? null,
            authorizationEnd: data.authorizedHours.authorizationEnd?.toDate?.() ?? null,
          }
        : undefined,
      assignedRbtIds: data.assignedRbtIds || [],
      primarySupervisorId: data.primarySupervisorId,
      assignedBcbaId: data.assignedBcbaId ?? data.primarySupervisorId,
      notes: data.notes,
      status: data.status,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      createdBy: data.createdBy,
    };
  }
}

// Singleton instance
export const clientService = ClientService.getInstance();
